// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.SavePopupAction.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

import { compose, setPropTypes, withHandlers, withState } from 'recompose';
import { PropTypes } from 'react';
import { action } from 'mobx';
import { isNil, isEmpty, curry } from 'lodash/fp';

import Constants from 'logic/data/Izenda.ReportDesignerConstants';
import reportsConstant from 'logic/data/Izenda.ReportsConstants';

export default compose(
  withState('reportName', 'setReportName', props => props.store.model.reportName),
  withState('originalReportName', 'setOriginalReportName', props => props.store.model.reportName),
  withState('originalReportType', 'setOriginalReportType', props => props.store.model.reportType),
  withState('originalCategoryId', 'setOriginalCategoryId', props => props.store.model.categoryId),
  withState('originalCategoryName', 'setOriginalCategoryName', props => props.store.model.categoryName),
  withState('originalSubCategoryId', 'setOriginalSubCategoryId', props => props.store.model.subCategoryId),
  withState('saveInto', 'changeSaveInto', props =>
    props.store.model.isGlobal ? Constants.GLOBAL_REPORT_ENUM.GLOBAL : Constants.GLOBAL_REPORT_ENUM.LOCAL
  ),
  withState('originalSaveInto', 'changeOriginalSaveInto', props => props.store.model.isGlobal),
  withHandlers({
    onClosing: curry(onClosing),
    onAcceptClosing: curry(onAcceptClosing),
    onCancelClosing: curry(action(onCancelClosing)),
    detectReportChange: curry(detectReportChange),
    saveReport: curry(saveReport),
    changeCategory: curry(changeCategory),
    addNewCategory: curry(addNewCategory),
    changeReportType: curry(changeReportType),
    changeSubCategory: curry(changeSubCategory),
    addNewSubCategory: curry(addNewSubCategory),
    updateSaveInto: curry(updateSaveInto)
  }),
  setPropTypes({
    store: PropTypes.object.isRequired,
    section: PropTypes.string,
    onSaved: PropTypes.func
  })
);

/**
 * Update "Save Into" in save popup
 * @param {any} props
 * @param {number} value 
 */
function updateSaveInto(props: any, value: number) {
  // Dropdown pass 2 arguments to onChange event, this makes error in withState (recompose)
  const { store, saveInto, store: {model} } = props;
  
  // change global/local with category 
  store.changeSaveInto(value).then(() => {
    if (saveInto !== value){
      props.changeSaveInto(value);
    }
  });

  // if nothing has changed, we should ensure that current category and subcategory will 
  // be shown properly.
  if (saveInto === value && !isNil(model.categoryId)) {
    if (isNil(model.subCategoryId))
      store.clearSubCategory();
    store.loadSubCategories(model.categoryId);
    return;
  }
  
  // Reset categories in case of changing global/local or empty categoryId
  store.clearCategory();
  store.clearSubCategory();
}

/**
 * Save report
 * @param {any} props
 * @return {promise}
 */
function saveReport(props: any) {
  const { store, section, fieldStore, confirmFn, saveAsMode, isNew, onSaved, disableReport, onReject } = props;

  store.model.setIgnoreCheckChange();

  // Save as in Report Viewer
  if (!section && fieldStore) {
    const reportBody = fieldStore.reportBody;
    if (reportBody.hasInvisibleFields) {
      return confirmFn('Confirmation', {
        description: reportsConstant.MESSAGES.SAVE_AS_REPORT,
        isHTMLFormat: true
      }).then(() => {
        return store.saveReport(Constants.PAGE_NAME.FIELDS).then(result => {
          (saveAsMode || isNew) && result.success && onSaved && onSaved(result.reportId);
          // Close Save As popup after confirmation popup saves report
          onReject();
        });
      });
    }
  }

  // Save as in Report Designer
  return store.saveReport(section).then(result => {
    result.isDisableReport && disableReport && disableReport();

    (saveAsMode || isNew) && result.success && onSaved && onSaved(result.reportId);
    return result;
  });
}

/**
 * Detect report change
 * @param {any} props
 * @return {promise}
 */
function detectReportChange(props: any) {
  const { store, section, disableReport, confirmFn } = props;

  return store.detectReportChange(section).then(result => {
    if (!result.success) {
      const error = result.messages[0];

      switch (error.key) {
        case Constants.DETECT_CHANGE.PHYSICAL:
          confirmFn('Information', {
            description: error.messages[0],
            type: 'info',
            showCloseIndicator: true,
            isHTMLFormat: true
          }).then(disableReport);
          break;
        case Constants.DETECT_CHANGE.LOGICAL:
          return confirmFn('Confirmation', {
            description: error.messages[0],
            isHTMLFormat: true
          }).then(saveReport.bind(null, props));
      }
      return null;
    }

    return saveReport(props);
  });
}

/**
 * When click button OK on popup
 * @param {any} props
 * @return {promise}
 */
function onAcceptClosing(props: any) {
  const { store, saveAsMode, validation, reportName, saveInto, section } = props;
  const model = store.model;
  saveAsMode && model.markSaveAs();
  const valManager = validation;

  return valManager
    .validate()
    .then(success => {
      if (success) {
        valManager.reset();
        return store.validate(reportName).then(messages => {
          if (!messages) {
            !!saveInto !== model.isGlobal && model.changeGlobalType(!!saveInto);
            model.changeName(reportName);
            return section ? detectReportChange(props) : saveReport(props);
          }
           valManager.updateErrors(messages);
        });
      }
      return false;
    })
    .then(saveResult => {
      if (saveResult && !isEmpty(saveResult.messages)) {
        valManager.updateErrors(saveResult.messages);
      } else if (saveResult) {
        store.model.resetSaveAs();
        store.setNewReport(false);
        props.onAccept();
      }
      return null;
    });
}

/**
 * When click button Cancel on popup
 * @param {any} props
 */
function onCancelClosing(props: any) {
  const {
    store,
    originalReportName,
    originalReportType,
    originalSaveInto,
    originalCategoryId,
    originalCategoryName,
    originalSubCategoryId,
    validation,
    onReject
  } = props;
  const model = store.model;

  model.changeName(originalReportName);
  model.updateType(originalReportType);
  store.changeSaveInto(originalSaveInto).then(() => {
    if (store.saveAsMode && store.model.isGlobal && !isNil(store.accessStore.tenantId)) {
      model.updateCategory(originalCategoryId, originalCategoryName);
    } else {
      store.changeCategory(originalCategoryId).then(() => {
        model.updateSubCategory(originalSubCategoryId);
      });
    }
  });

  validation.reset();
  onReject();
  store.model.resetSaveAs();
}

/**
 * Handle when popup closing
 * @param {any} props
 * @param  {Function} closeCallback
 * @param  {string} type
 */
function onClosing(props: any, closeCallBack: () => mixed, type: string) {
  if (type === 'accept') {
    onAcceptClosing(props);
  } else if (type === 'cancel') {
    onCancelClosing(props);
  }
}

/**
 * Change category for report
 * @param {any} props
 * @param  {string} id
 * @param  {string} name
 */
function changeCategory(props: any, id: string, name: string) {
  props.store.changeCategory(id, name);
}

/**
 * Add new category for report
 * @param {any} props
 * @param  {any} value
 */
function addNewCategory(props: any, value: any) {
  props.store.model.addCategory(value);
}

/**
 * Change type of report
 * @param {any} props
 * @param  {number} value
 */
function changeReportType(props: any, value: number) {
  const { validation, store, reportName, setReportName } = props;
  validation.reset();
  store.changeType(value);

  if (
    store.isNewReport &&
    (reportName === Constants.DEFAULT_REPORT_NAME || reportName === Constants.DEFAULT_TEMPLATE_NAME)
  ) {
    const isReportType = store.model.reportType === Constants.REPORT_TYPE_ENUM.REPORT;
    const reportDefaultName = isReportType ? Constants.DEFAULT_REPORT_NAME : Constants.DEFAULT_TEMPLATE_NAME;
    setReportName(reportDefaultName);
  }
}

/**
 * Change subCategory for report
 * @param {any} props
 * @param  {string} id
 * @param  {string} name
 */
function changeSubCategory(props: any, id: string, name: string) {
  props.store.model.updateSubCategory(id, name);
}

/**
 * Add new subCategory for report
 * @param {any} props
 * @param  {any} value
 */
function addNewSubCategory(props: any, value: any) {
  props.store.model.addSubCategory(value);
}



// WEBPACK FOOTER //
// ./ui/modules/ReportDesigner/SavePopup/Izenda.SavePopupAction.js